import { z } from "zod";
import {
  BRAZIL_UFS,
  COPILOT_METRICS,
  METRICS_GROUPS,
  METRICS_PERIODS,
  SQL_MAX_CHARS,
  normalizeBrPhone,
  type CopilotMetric,
  type MetricsGroup,
} from "@cx/domain";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import { refinePair } from "./dashboard";

// ===== Copiloto somente leitura (T1.14, 06-ia-agentes) =====

export const COPILOT_MESSAGE_MAX = 2000;

/** Pergunta do painel. `context.route` = tela atual (só o caminho, sem query). */
export const copilotChatInput = z.object({
  conversationId: ulidParam.optional(),
  message: z
    .string("Escreva a pergunta")
    .trim()
    .min(1, "Escreva a pergunta")
    .max(COPILOT_MESSAGE_MAX, `No máximo ${COPILOT_MESSAGE_MAX} caracteres`),
  context: z
    .object({
      route: z
        .string()
        .max(200)
        .regex(/^\/[A-Za-z0-9/_-]*$/, "Rota inválida")
        .optional(),
    })
    .optional(),
});
export type CopilotChatInput = z.output<typeof copilotChatInput>;

export const COPILOT_TOOLS = ["query_metrics", "search_entities", "run_readonly_sql"] as const;
export type CopilotToolName = (typeof COPILOT_TOOLS)[number];

/** Argumentos do `query_metrics` (vindos do modelo). Datas: as duas ou nenhuma, até 366 dias. */
export const queryMetricsArgs = z
  .object({
    metrics: z
      .array(z.enum(COPILOT_METRICS))
      .min(1, "Escolha ao menos uma métrica")
      .max(6, "No máximo 6 métricas")
      .default(["revenue", "orders"])
      .describe(
        "revenue = valor total vendido (com frete); net_revenue = sem frete; orders = pedidos; items = peças; avg_ticket = ticket médio; cost, gross_profit, gross_margin, profit = custo, lucro bruto, margem bruta e lucro líquido (só se disponíveis)",
      ),
    period: z
      .enum(METRICS_PERIODS)
      .optional()
      .describe("Atalho de período (padrão: this_month). Ignorado quando from e to vierem."),
    from: businessDate.optional().describe("Início AAAA-MM-DD"),
    to: businessDate.optional().describe("Fim AAAA-MM-DD (até 366 dias)"),
    groupBy: z
      .enum(METRICS_GROUPS)
      .default("none")
      .describe("Agrupar por dia, mês, canal, vendedor, UF, situação ou produto"),
    compare: z.boolean().default(false).describe("Comparar com o período anterior de mesmo tamanho"),
    uf: z.enum(BRAZIL_UFS).optional().describe("Só pedidos desta UF"),
    limit: z.number().int().min(1).max(31).default(10).describe("Máximo de linhas no agrupamento"),
  })
  .superRefine((v, ctx) => refinePair(v, ctx, "from", "to", "do período"));
export type QueryMetricsArgs = z.output<typeof queryMetricsArgs>;

export const SEARCH_ENTITY_TYPES = [
  "contacts",
  "products",
  "sales_orders",
  "receivables",
  "payables",
] as const;
export type SearchEntityType = (typeof SEARCH_ENTITY_TYPES)[number];
export const searchEntitiesArgs = z.object({
  type: z.enum(SEARCH_ENTITY_TYPES).describe("O que procurar"),
  query: z.string().trim().min(1, "Informe o termo").max(100).describe("Nome, código, SKU, GTIN ou número"),
  limit: z.number().int().min(1).max(10).default(5),
});
export type SearchEntitiesArgs = z.output<typeof searchEntitiesArgs>;

export const runReadonlySqlArgs = z.object({
  sql: z
    .string()
    .min(1, "Informe a consulta")
    .max(SQL_MAX_CHARS, `No máximo ${SQL_MAX_CHARS} caracteres`)
    .describe("Um SELECT sobre as visões listadas (sem subconsultas)"),
});
export type RunReadonlySqlArgs = z.output<typeof runReadonlySqlArgs>;

export const copilotToolCall = z.discriminatedUnion("name", [
  z.object({ name: z.literal("query_metrics"), args: queryMetricsArgs }),
  z.object({ name: z.literal("search_entities"), args: searchEntitiesArgs }),
  z.object({ name: z.literal("run_readonly_sql"), args: runReadonlySqlArgs }),
]);
export type CopilotToolCall = z.output<typeof copilotToolCall>;

/**
 * O que o usuário pode ler, calculado pela API a partir das permissões (padrão seguro = nada). O DO confere cada
 * ferramenta contra isto; a carteira do usuário-vendedor vem no RequestCtx.
 */
export type CopilotGrants = {
  dashboard?: boolean;
  /** products:read_cost: custo, margem e lucro. */
  cost?: boolean;
  /** sellers:read: agrupar/filtrar por vendedor e a visão de vendedores. */
  sellers?: boolean;
  contacts?: boolean;
  products?: boolean;
  stock?: boolean;
  warehouses?: boolean;
  salesOrders?: boolean;
  receivables?: boolean;
  payables?: boolean;
  statuses?: boolean;
};

export type MetricCell = {
  value: number | null;
  text: string;
  previous?: number | null;
  variationPct?: string | null;
};
export type MetricsRow = { key: string; label: string; values: Partial<Record<CopilotMetric, MetricCell>> };
export type QueryMetricsResult = {
  period: { from: string; to: string };
  compare: { from: string; to: string } | null;
  statuses: string[];
  groupBy: MetricsGroup;
  totals: Partial<Record<CopilotMetric, MetricCell>>;
  rows: MetricsRow[];
  notes: string[];
};
export type SearchEntitiesResult = {
  type: SearchEntityType;
  items: Record<string, unknown>[];
  more: boolean;
};
export type ReadonlySqlResult = {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  truncated: boolean;
  limit: number;
};
export type CopilotToolResult = QueryMetricsResult | SearchEntitiesResult | ReadonlySqlResult;

export type CopilotMessageView = {
  id: string;
  role: "user" | "assistant";
  content: string;
  tools: string[];
  createdAt: string;
};
export type CopilotConversationView = {
  id: string;
  channel: "web" | "whatsapp";
  title: string | null;
  updatedAt: string;
};

// ===== WhatsApp (RF-BI-13) =====

export const whatsappLinkInput = z.object({
  phone: z
    .string("Informe o número")
    .max(30, "Número inválido")
    .transform((v, ctx) => {
      const e164 = normalizeBrPhone(v);
      if (!e164) {
        ctx.addIssue({ code: "custom", message: "Use um celular brasileiro com DDD, ex.: (11) 98765-4321" });
        return z.NEVER;
      }
      return e164;
    }),
});
export type WhatsappLinkInput = z.output<typeof whatsappLinkInput>;

export const whatsappSandboxInput = z.object({
  text: z
    .string("Escreva a mensagem")
    .trim()
    .min(1, "Escreva a mensagem")
    .max(1000, "No máximo 1.000 caracteres"),
});

export type WhatsappLinkStatus = {
  /** `disabled` = canal desligado neste ambiente. */
  provider: "meta" | "sandbox" | "disabled";
  /** Número do CX ERP para onde o usuário manda o código e as perguntas. */
  botNumber: string | null;
  link: { phone: string; companyId: string | null; verifiedAt: string | null } | null;
  pending: { phone: string; expiresAt: string } | null;
};
export type WhatsappLinkCreated = {
  phone: string;
  code: string;
  expiresAt: string;
  botNumber: string | null;
};
export type WhatsappSandboxMessage = { to: string; text: string; at: string };
